import LearnLayout, { LEARN_PAGES } from './LearnLayout'

const page = LEARN_PAGES.find((p) => p.slug === 'endometriosis')!

export default function EndometriosisPage() {
  return (
    <LearnLayout page={page}>
      <h2 id="seeds">
        <span className="learn-act">Act 1</span>
        The seeds
      </h2>
      <p>
        Every month when you menstruate, some of that blood flows backward through your fallopian
        tubes into your pelvis instead of out through your cervix. This is called{' '}
        <strong>retrograde menstruation</strong>, and here&rsquo;s the wild part: it happens in
        about 90% of women. Most of them never develop endometriosis.
      </p>
      <p>So retrograde flow isn&rsquo;t the disease. It&rsquo;s just how the seeds get planted.</p>
      <p>The disease is what happens next.</p>

      <h2 id="immune-failure">
        <span className="learn-act">Act 2</span>
        The immune failure
      </h2>
      <p>
        In a healthy body, the immune system sees those wayward endometrial cells in the pelvis and
        clears them. Natural killer (NK) cells patrol, macrophages clean up, and that&rsquo;s the
        end of it.
      </p>
      <p>
        In endometriosis, that clearance system breaks down. NK cells in the peritoneal fluid are
        dysfunctional. They see the invaders but don&rsquo;t attack. Macrophages switch from
        &ldquo;clean this up&rdquo; mode to &ldquo;tolerate this&rdquo; mode. The immune system
        essentially waves the ectopic cells through. The seeds land, and nobody removes them.
      </p>
      <p>
        That&rsquo;s why you can have endometriosis with perfectly normal anatomy. The problem
        isn&rsquo;t structural. It&rsquo;s immunological.
      </p>

      <h2 id="fuel">
        <span className="learn-act">Act 3</span>
        The fuel
      </h2>
      <p>
        Once those cells implant, they do something normal uterine tissue doesn&rsquo;t do: they
        start producing their own estrogen.
      </p>
      <p>
        Ectopic endometrial cells express an enzyme called <strong>aromatase (CYP19A1)</strong>.
        Aromatase converts androgens into estradiol locally, right at the lesion site. That
        estradiol then upregulates <strong>COX-2</strong>, which produces{' '}
        <strong>prostaglandin E2 (PGE2)</strong>. And PGE2 further induces aromatase.
      </p>
      <p>
        It&rsquo;s a self-sustaining feedback loop. The lesion makes its own fuel. It doesn&rsquo;t
        need your ovaries to survive.
      </p>
      <p>
        This is why women who are surgically menopausal can still have active disease. This is why
        simply suppressing ovarian estrogen doesn&rsquo;t fully eradicate it. The lesions have gone
        off-grid.
      </p>

      <h2 id="progesterone">
        <span className="learn-act">Act 4</span>
        The escape from progesterone
      </h2>
      <p>
        Normally, progesterone is the hormone that keeps the uterine lining in check. It drives
        programmed cell death, limits estrogen&rsquo;s effects, and keeps things balanced.
      </p>
      <p>
        In endometriosis, this system is broken too. There is a striking loss of a progesterone
        receptor subtype called <strong>PR-B</strong>, the one that actually mediates most of
        progesterone&rsquo;s effects. What remains is <strong>PR-A</strong>, which not only fails to
        respond to progesterone but actively blocks any residual PR-B from functioning.
      </p>
      <p>
        This is called <strong>progesterone resistance</strong>. The receptor is present. The
        hormone is present. The lock and key are both there, but the key no longer turns anything.
      </p>
      <p>
        This is why progestins help manage symptoms but don&rsquo;t cure the disease. The mechanism
        driving that resistance? <strong>Epigenetic silencing</strong>: the PR-B gene gets
        methylated and shut off in endometriotic lesions, permanently, at the DNA level.
      </p>

      <h2 id="firestorm">
        <span className="learn-act">Act 5</span>
        The firestorm
      </h2>
      <p>
        With no immune clearance, autonomous estrogen production, and progesterone resistance all
        running simultaneously, the pelvic environment becomes chronically inflamed. Cytokines like
        IL-6 and TNF-&alpha; drive further implantation. VEGF builds new blood vessels to feed the
        lesions. Matrix metalloproteinases (MMPs) allow the tissue to invade deeper structures.
      </p>
      <p>
        Over time this creates adhesions that glue organs together, obliterated anatomy, ovarian
        cysts called <strong>endometriomas</strong> (chocolate cysts, filled with old blood from
        cyclical bleeding), and in severe cases deep infiltrating disease invading the uterosacral
        ligaments, the space between the rectum and vagina, the bowel, and the bladder.
      </p>

      <h2 id="why-it-hurts">Why it hurts so much</h2>
      <p>
        Pain in endometriosis isn&rsquo;t just inflammation. The lesions develop{' '}
        <strong>direct nerve fiber ingrowth</strong>: sensory nerve fibers and sympathetic fibers
        actually grow into the lesions themselves. Over time, the central nervous system upregulates
        its pain processing through a phenomenon called <strong>central sensitization</strong>,
        where the brain becomes hyperresponsive to pain signals from the pelvis. This is why pain
        often persists even after surgical removal of visible lesions, and why the same amount of
        disease can cause wildly different levels of pain in different women.
      </p>

      <div className="learn-bottom-line">
        <span className="learn-bottom-line-label">The bottom line</span>
        <p>
          Endometriosis is not &ldquo;bad periods.&rdquo; It is a systemic inflammatory disease
          driven by immune failure, a self-sustaining estrogen loop, epigenetic progesterone
          resistance, and progressive nerve sensitization. It affects an estimated 1 in 10 women in
          their reproductive years, takes an average of 7 years to diagnose, and has no cure: only
          management. The gold standard treatment for the disease itself (not just symptoms) is{' '}
          <strong>excision surgery</strong>: physically removing lesions at the root.
        </p>
      </div>
    </LearnLayout>
  )
}
